import { useDialogAlert } from '@app/components/DialogAlert';
import ModalResponsive from '@app/components/ModalResponsive';
import { useClock } from '@app/contexts/Clock';
import { EmailService } from '@app/services/email-service';
import { isProduction } from '@app/utils/environment';
import { Random } from '@app/utils/random';
import { generateWhatsappLink } from '@app/utils/whatsapp';
import { addDays } from 'date-fns';
import React, { FormEvent, useEffect, useState } from 'react';
import { useRestaurantContext } from '../context';
import {
  ButtonDelayControl,
  ButtonDelayControlLabel,
  ButtonsRow,
  FeedbackModalContent,
  FeedbackModalDescription,
  FeedbackModalForm,
  FeedbackModalMessageField,
  FeedbackModalSubmitButton,
  FeedbackModalTitle,
  FeedbackModalWarningMessage,
} from './styles';

type Props = {
  isVisible: boolean;
  onClose: () => void;
  onEmailSended: () => void;
};

export default function FeedbackModal({ isVisible, onClose, onEmailSended }: Props) {
  const clock = useClock();
  const { primaryColor, secondaryColor, cliqueiAcheiData, restaurant } = useRestaurantContext();

  const { DialogAlert, dialogRef } = useDialogAlert();

  const [delay, setDelay] = useState(Random.int(2, 10));

  useEffect(() => {
    if (isVisible) setDelay(Random.int(2, 10));
  }, [isVisible]);

  const name = cliqueiAcheiData.NomePuro;
  const email = cliqueiAcheiData.Email[0];
  const whatsapp = restaurant.whatsapp;

  function handleFormSubmit(event: FormEvent) {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const { message, sendOnDate } = parseFormData();

    if (!message) {
      alert('Digite sua mensagem para continuar!');
      return;
    }

    if (message.includes('?')) {
      dialogRef.current?.alert({
        title: 'Atenção!',
        message: alertMessageWhenAsking(),
        buttons: alertButtonsWhenAsking(),
      });
      return;
    }

    EmailService.send({
      to: isProduction() ? [{ email }] : [],
      bcc: EmailService.defaultEmails(),
      subject: `Feedback Anônimo para ${name}`,
      html: html(),
      sendOnDate,
    });

    form.reset();

    onEmailSended();

    function parseFormData() {
      return {
        message: new FormData(form).get('message')?.toString() ?? '',
        sendOnDate: delay > 0 ? addDays(clock.now() ?? new Date(), delay) : undefined,
      };
    }

    function html() {
      return `
        <h3>Para proteger a privacidade do usuário, os Feedbacks Anônimos não são enviados no dia da ocorrência.</h3>
        <strong>Mensagem Anônima:</strong>
        <p style="margin-top: 2px;">${message}</p>
      `;
    }

    function alertButtonsWhenAsking() {
      const result = [
        { text: 'Não' },
        {
          text: 'Sim',
          link: { href: generateWhatsappLink(whatsapp, `*Enviado do Cliquei Achei:* ${message}`), target: '_blank' },
        },
      ];
      const okButton = [{ text: 'Ok' }];
      return whatsapp ? result : okButton;
    }

    function alertMessageWhenAsking() {
      let result = `Você está enviando uma pergunta, mas esta mensagem é anônima e a ${name} não tem como te responder.`;
      if (whatsapp) result += ' Gostaria de enviar esta mensagem no WhatsApp do estabelecimento?';
      return result;
    }
  }

  const minusButtonIsDisabled = delay === 0;

  return (
    <ModalResponsive
      open={isVisible}
      onClose={onClose}
      appBarConfig={{ title: '', color: primaryColor, tintColor: secondaryColor }}
      desktopWidthContent={500}
      desktopMaxHeightContent="80%"
      desktopHeightContent="fit-content"
    >
      <FeedbackModalContent>
        <FeedbackModalTitle>Enviar Mensagem Anônima para {name}</FeedbackModalTitle>
        <FeedbackModalDescription>
          Algo não lhe agradou? A direção da {name} ficará feliz em saber o que aconteceu para que o problema não ocorra
          novamente. Sua mensagem será enviada do nosso e-mail de forma totalmente anônima para o estabelecimento
          comercial, ou seja, é impossível saber que foi você que enviou.
        </FeedbackModalDescription>
        <FeedbackModalForm onSubmit={handleFormSubmit}>
          <FeedbackModalMessageField name="message" placeholder="Sua mensagem" />
          <ButtonsRow>
            <FeedbackModalSubmitButton label={minusButtonIsDisabled ? 'Enviar agora' : `Enviar daqui ${delay} dias`} />
            <ButtonDelayControl disabled={minusButtonIsDisabled} type="button" onClick={() => setDelay(delay - 1)}>
              <ButtonDelayControlLabel>-</ButtonDelayControlLabel>
            </ButtonDelayControl>
            <ButtonDelayControl type="button" onClick={() => setDelay(delay + 1)}>
              <ButtonDelayControlLabel>+</ButtonDelayControlLabel>
            </ButtonDelayControl>
          </ButtonsRow>
          <FeedbackModalWarningMessage>
            Não se preocupe! As mensagens anônimas são enviadas em dias aleatórios para que o estabelecimento comercial
            não consiga identificar o dia que ocorreu o problema.
          </FeedbackModalWarningMessage>
        </FeedbackModalForm>
        {DialogAlert}
      </FeedbackModalContent>
    </ModalResponsive>
  );
}
